/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useState } from "react";
import ReimbursementList from "./ReimbursementList";

import reimbursementManager from "services/Reimbursement/reimbursementManager";

import { FaEdit, FaEye, FaRegTrashAlt } from "react-icons/fa";
import UpdateReimbursement from "./updateReimbursement/UpdateReimbursement";
import GenericModal from "components/Modal";
import {
  ReimbursementListResponse,
  ReimbursementResponse,
} from "services/Reimbursement/reimbursementInterface";
import ViewReimbursement from "./viewReimbursement/ViewReimbursement";
import DeletePopup from "components/DeletePopup/deletePopup";
import GenericToaster from "components/Toaster";
import { useTranslation } from "react-i18next";
import formatCurrency from "util/currency";
import { useAppContext } from "contexts/useAppContext";
import apiService from "services/apiService";
import HeaderButtons from "components/Button/HeaderButtons";
import {
  reimbursementStatus,
  reimbursementType,
} from "./AddReimbursement/ReimbursementInterface";
import { dateFormate } from "util/Util";

export const statusOptions = [
  { value: "INITIATED", label: "Initiated" },
  { value: "APPROVED", label: "Approved" },
  { value: "REJECTED", label: "Rejected" },
  { value: "PENDING", label: "Pending" },
  { value: "PAID", label: "Paid" },
];

const statusApproveOptions = [
  { value: "APPROVED", label: "Approved" },
  { value: "PAID", label: "Paid" },
];

const isPaginatedReimbursementResponse = (
  payload: ReimbursementResponse[] | ReimbursementListResponse,
): payload is ReimbursementListResponse => {
  return !Array.isArray(payload);
};

export default function Reimbursement() {
  const [reimbursement, setReimbursement] = useState<any>([]);
  const [originalReimbursement, setOriginalReimbursement] = useState<any[]>([]);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState();
  const [loading, setLoading] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [openDeleteModal, setDeleteModal] = useState(false);
  const [showSuccessToaster, setSuccessToaster] = useState(false);
  const [showErrorToaster, setErrorToaster] = useState(false);
  const [message, setMessage] = useState("");
  const [editedRows, setEditedRows] = useState<any[]>([]);
  const [filters, setFilters] = useState<{
    username: string;
    status: string;
    type: string;
    fromDate: string;
    toDate: string;
  }>({ username: "", status: "", type: "", fromDate: "", toDate: "" });
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const { userRole, user } = useAppContext();
  const { t } = useTranslation();
  const [userList, setUserList] = useState<{ value: string; label: string }[]>(
    []
  );

  const handleEdit = (row: any): void => {
    setOpenEditModal(true);
    setSelectedRow(row);
  };
  const handleDelete = (row: any): void => {
    setDeleteModal(true);
    setSelectedRow(row);
  };

  const handleView = (row: any): void => {
    setViewModal(true);
    setSelectedRow(row);
  };

  const handleModalClose = () => {
    setOpenEditModal(false);
    setViewModal(false);
  };

  const onSuccess = (message: string) => {
    getReimbursementData();
    setSuccessToaster(true);
    setMessage(message);
  };

  const onError = (message: string) => {
    setErrorToaster(true);
    setMessage(message);
  };

  const deleteRow = (id: any) => {
    setDeleteModal(false);
    reimbursementManager
      .deleteReimbursement(id)
      .then((res) => {
        if (res.status === 200) {
          getReimbursementData();
          setSuccessToaster(true);
          setMessage("Deleted successfully");
        } else {
          setErrorToaster(true);
          setMessage(res.statusText || "Something went wrong");
        }
      })
      .catch((err) => {
        setErrorToaster(true);
        setMessage(err || "Something went wrong");
      });
  };

  const closeToaster = () => {
    setErrorToaster(false);
    setSuccessToaster(false);
  };
  //sonar qube deep level fixes

  const calculateApproveAmount = (row: any) => {
    const isApprovedStatus =
      row.status === "APPROVED" ||
      row.status === "PENDING" ||
      row.status === "PAID";

    if (row.approveAmount && isApprovedStatus) {
      return row.approveAmount;
    }

    return row.submitAmount - row.approveAmount;
  };

  const mapReimbursementRow = (row: any) => {
    const isOwnReimbursement =
      userRole === 2 && user.displayName !== row.username;

    return {
      ...row,
      isCanEditStatus: isOwnReimbursement,
      paidAmount: row.approveAmount || 0,
      approveAmount: calculateApproveAmount(row),
      differenceAmount:
        row.differenceAmount ??
        row.submitAmount - (row.approveAmount || 0),
    };
  };

  const getUsersFromData = (data: any[]) => {
    return Array.from(
      new Set(
        data
          .map((item: any) => item?.username)
          .filter(
            (name: any) => typeof name === "string" && name.trim()
          )
      )
    ).map((name) => ({
      value: name as string,
      label: name as string,
    }));
  };

  const mergeUniqueUsers = (
    prev: { value: string; label: string }[],
    users: { value: string; label: string }[]
  ) => {
    const merged = [...prev, ...users];
    const uniqueMap = new Map(
      merged.map((item) => [item.value, item])
    );

    return Array.from(uniqueMap.values());
  };

  const getApiData = (apiData: any) => {
    let data: any[] = [];

    if (Array.isArray(apiData)) {
      data = apiData;
    } else if (Array.isArray(apiData.content)) {
      data = apiData.content;
    }

    // data.sort((a: any, b: any) => b.id - a.id);

    return data;
  };

  const getPaginationData = (apiData: any, data: any[]) => {
    const apiTotalElements =
      isPaginatedReimbursementResponse(apiData) &&
        typeof apiData.totalElements === "number"
        ? apiData.totalElements
        : data.length;

    const apiTotalPages =
      isPaginatedReimbursementResponse(apiData) &&
        typeof apiData.totalPages === "number"
        ? apiData.totalPages
        : Math.ceil(apiTotalElements / size);

    return {
      apiTotalElements,
      apiTotalPages,
    };
  };

  const getReimbursementData = async () => {
    setLoading(true);

    try {
      const res = await reimbursementManager.getReimbursementData({
        page,
        size,
        status: filters.status || undefined,
        username: filters.username || search || undefined,
        fromDate: filters.fromDate || undefined,
        toDate: filters.toDate || undefined,
        type: filters.type
          ? filters.type.toLowerCase()
          : undefined,
        // username: search || undefined,
      });

      if (res.status !== 200) {
        return;
      }

      const apiData = res.data;

      const data = getApiData(apiData);

      const {
        apiTotalElements,
        apiTotalPages,
      } = getPaginationData(apiData, data);

      const mappedData = data.map(mapReimbursementRow);

      setReimbursement(mappedData);
      setOriginalReimbursement(mappedData);

      setTotalElements(apiTotalElements);
      setTotalPages(apiTotalPages);

      const users = getUsersFromData(data);

      setUserList((prev) => mergeUniqueUsers(prev, users));
    } catch (error) {
      console.log("Error fetching reimbursements", error);
    } finally {
      setLoading(false);
    }
  };
  //

  const handleApproveAmountChange = (row: any, newValue: string) => {
    const updated = reimbursement.map((item: any) =>
      item.id === row.id ? { ...item, approveAmount: Number(newValue) } : item
    );
    setReimbursement(updated);
    updateEditedRows(row.id, { ...row, approveAmount: Number(newValue) });
  };
  const handleStatusChange = (row: any, newStatus: string) => {
    const updated = reimbursement.map((item: any) =>
      item.id === row.id ? { ...item, status: newStatus } : item
    );
    setReimbursement(updated);

    // Find the original row to check if status has changed
    const originalRow = originalReimbursement.find(
      (item: any) => item.id === row.id
    );
    if (originalRow && originalRow.status !== newStatus) {
      updateEditedRows(row.id, { ...row, status: newStatus });
    } else {
      // If status reverted to original, remove from edited rows
      setEditedRows((prev) => prev.filter((item) => item.id !== row.id));
    }
  };
  const updateEditedRows = (id: any, updatedRow: any) => {
    setEditedRows((prev) => {
      const exists = prev.find((item) => item.id === id);
      if (exists) {
        return prev.map((item) => (item.id === id ? updatedRow : item));
      } else {
        return [...prev, updatedRow];
      }
    });
  };

  const isStatusLocked = (row: any, originalRow: any) => {
    return !row.isCanEditStatus || originalRow?.status === "PAID";
  };

  const getEditableStatusOptions = (originalStatus?: string) => {
    if (originalStatus === "APPROVED") {
      return statusApproveOptions;
    }
    return statusOptions;
  };

  const canShowEditButton = (row: any) => {
    if (userRole === 2) {
      return (
        row.status === "INITIATED" ||
        row.status === "PENDING" ||
        row.status === "APPROVED" ||
        row.status === "REJECTED"
      );
    }

    return userRole !== 2 && row.status === "INITIATED";
  };

  const renderEditButton = (row: any) => {
    if (!canShowEditButton(row)) {
      return null;
    }



    return (
      <span
        onClick={() => handleEdit(row as ReimbursementResponse)}
        className="btn-primary px-2 py-2 mr-2 rounded-lg"
        title="Edit"
      >
        <FaEdit />
      </span>
    );
  };

  const tableColumns = [
    // { key: "id", label: `ID`, bold: true },
    { key: "username", label: `Name`, bold: true },
    { key: "type", label: `${t("reimbursement.type")}`, bold: true },
    {
      key: "spentDate",
      label: `${t("reimbursement.spentDate")}`,
      // type: "Date",
      // bold: true,
      render: (value: any) => dateFormate(String(value)),
    },
    {
      key: "unit",
      label: `${t("reimbursement.unit")}`,
      bold: true,
      render: (row: any) => {
        return row === "INR" ? (
          <span dangerouslySetInnerHTML={{ __html: "&#x20B9;" }} />
        ) : (
          "$"
        );
      },
    },
    {
      key: "submitAmount",
      label: `Actual`,
      render: (value: any, row: any) => {
        return `${formatCurrency(value, row.unit)}`;
      },
    },
    {
      key: "approveAmount",
      label: `${t("reimbursement.approveAmount")}`,
      bold: true,
      render: (value: any, row: any) => {
        const originalRow = originalReimbursement.find((r) => r.id === row.id);
        if (!originalRow) return null;

        return (
          <div className="relative flex items-center">
            {/* Positioned the symbol slightly inward */}
            <span className="absolute left-3.5 text-slate-500 dark:text-slate-400 text-sm font-medium pointer-events-none">
              {row.unit === "INR" ? (
                <span dangerouslySetInnerHTML={{ __html: "&#x20B9;" }} />
              ) : (
                "$"
              )}
            </span>
            <input
              type="number"
              value={row.approveAmount}
              onChange={(e) => handleApproveAmountChange(row, e.target.value)}
              /* Increased padding-left to pl-10 to prevent overlapping */
              className={`!rounded-xl border-slate-200 dark:border-slate-700 bg-white dark:bg-gray-800 py-1.5 pr-3 pl-10 w-32 text-sm font-medium focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all ${originalRow?.status === "PAID" ||
                  !row.isCanEditStatus ||
                  originalRow?.status === "APPROVED" ||
                  originalRow?.status === "REJECTED"
                  ? "!bg-slate-100 dark:!bg-slate-800/50 cursor-not-allowed text-slate-500"
                  : ""
                }`}
              disabled={
                originalRow?.status === "PAID" ||
                !row.isCanEditStatus ||
                originalRow?.status === "APPROVED" ||
                originalRow?.status === "REJECTED"
              }
              onWheel={(e) => (e.target as HTMLInputElement).blur()}
            />
          </div>
        );
      },
    },

    {
      key: "differenceAmount",
      label: `${t("reimbursement.differenceAmount")}`,
      bold: true,
      render: (value: any, row: any) => {
        return `${formatCurrency(value, row.unit)}`;
      },
    },
    {
      key: "status",
      label: t("reimbursement.status"),
      bold: true,
      render: (_: any, row: any) => {
        const getStatusColor = (status: string) => {
          switch (status) {
            case "INITIATED":
              return "bg-primary/10 text-primary border-primary/20 dark:bg-primary/20 dark:border-primary/30";
            case "PENDING":
              return "bg-warning/10 text-warning border-warning/20 dark:bg-warning/20 dark:border-warning/30";
            case "APPROVED":
              return "bg-success/10 text-success border-success/20 dark:bg-success/20 dark:border-success/30";
            case "REJECTED":
              return "bg-error/10 text-error border-error/20 dark:bg-error/20 dark:border-error/30";
            case "PAID":
              return "bg-secondary/10 text-secondary border-secondary/20 dark:bg-secondary/20 dark:border-secondary/30";
            default:
              return "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800/50 dark:text-slate-400 dark:border-slate-700";
          }
        };

        // Get the original row to determine which options to show
        const originalRow = originalReimbursement.find((r) => r.id === row.id);

        return (
          <select
            value={row.status}
            onChange={(e) => handleStatusChange(row, e.target.value)}
            disabled={isStatusLocked(row, originalRow)}
            className={`!rounded-xl border px-3 py-1.5 font-semibold text-xs tracking-wide transition-all ${isStatusLocked(row, originalRow)
              ? "opacity-60 cursor-not-allowed bg-slate-100 dark:bg-slate-800/50"
              : "cursor-pointer hover:shadow-md active:scale-95"
              } ${getStatusColor(row.status)}`}
          >
            {getEditableStatusOptions(originalRow?.status).map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        );
      },
    },
    {
      key: "actions",
      label: `${t("reimbursement.actions")}`,
      excludeExport: true,
      actions: (row: any) => (
        <div className="flex flex-start">
          {renderEditButton(row)}

          <span
            onClick={() => handleView(row)}
            className="btn-primary px-2 py-2 mr-2 rounded-lg"
            title="View"
          >
            <FaEye />
          </span>
          {(row.status === "INITIATED" || row.status === "PENDING") && (
            <button
              onClick={() => handleDelete(row)}
              // className="p-2 transition-all hover:bg-error/10 rounded-xl group"
              title="Delete"
              className="btn-red px-2 py-2 rounded-lg"

            >
              <FaRegTrashAlt className="text-error group-hover:scale-110 transition-transform" />
            </button>
          )}
        </div>
      ),
    },
  ];

  useEffect(() => {
    if (userRole && user?.displayName) {
      getReimbursementData();
    }
  }, [
    userRole,
    user?.displayName,
    page,
    size,
    filters.status,
    filters.username,
    filters.fromDate,
    filters.toDate,
    filters.type,
    search,
  ]);



  const handleFilterApply = useCallback((newFilters: any) => {
    setFilters(newFilters);
    setPage(0);
  }, []);

  // Free-text search from the listing search box — reset to first page so
  // results start from the top for the new term.
  const handleSearch = useCallback((term: string) => {
    setSearch(term.trim());
    setPage(0);
  }, []);

  const handlePageChange = useCallback((nextPage: number) => {
    setPage(nextPage);
  }, []);

  const handleSizeChange = useCallback((nextSize: number) => {
    setSize((prev) => (prev === nextSize ? prev : nextSize));
    setPage(0);
  }, []);

  const onApprove = async () => {
    try {
      // Calculate difference amount for each edited row
      const updatedEditedRows = editedRows.map((row: any) => {
        const originalRow = originalReimbursement.find((r) => r.id === row.id);
        console.log("Original Row :>> ", originalRow);
        const currentRow = reimbursement.find((r: any) => r.id === row.id);
        console.log("Current Row :>> ", currentRow);

        if (originalRow && currentRow) {
          // Get the approve amount from the current row (what user entered in the table)
          const approveAmount = currentRow.approveAmount || 0;
          // Calculate difference: submitAmount - approveAmount
          const differenceAmount = originalRow.submitAmount - approveAmount;

          return {
            ...row,
            approveAmount: approveAmount,
            differenceAmount: differenceAmount,
            submitAmount: originalRow.submitAmount,
          };
        }
        return row;
      });
      console.log("Updated Edited Rows :>> ", updatedEditedRows);

      const formData = new FormData();
      const blob = new Blob([JSON.stringify(updatedEditedRows)], {
        type: "application/json",
      });
      formData.append("reimbursements", blob);

      const response = await apiService.put(
        "/api/reimbursement/bulk",
        formData
      );
      if (response.status === 200) {
        setSuccessToaster(true);
        setMessage("Reimbursements approved successfully");
        setEditedRows([]);
        getReimbursementData();
      } else {
        setErrorToaster(true);
        console.log("Error response :>> ", response);
        setMessage("Something went wrong");
      }
    } catch (err: any) {
      console.log("err :>> ", err);
      const errorMessage = err.response.data || "Something went wrong";
      setErrorToaster(true);
      setMessage(errorMessage);
    }
  };

  return (
    <div className="overflow-hidden text-slate-900 dark:text-white  bg-white dark:bg-gray-800">
      <div className="flex-1 min-h-0">
        <ReimbursementList
          data={reimbursement}
          columns={tableColumns}
          loading={loading}
          onSuccess={onSuccess}
          onError={onError}
          // title="Reimbursement List"
          onApply={handleFilterApply}
          statusList={reimbursementStatus}
          typeList={reimbursementType}
          userList={userList}
          currentFilters={filters}
          page={page}
          size={size}
          totalElements={totalElements}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          onSizeChange={handleSizeChange}
          onSearchTermChange={handleSearch}
          customButton={
            userRole === 2 && (
              <HeaderButtons
                onClick={onApprove}
                className={`${editedRows.length === 0
                  ? "opacity-50 cursor-not-allowed bg-slate-300 dark:bg-slate-800 text-slate-500"
                  : "bg-primary hover:bg-primary-hover shadow-lg shadow-primary/20"
                  } transition-all duration-300 font-bold px-6 py-2.5 rounded-xl`}
                disabled={editedRows.length === 0}
              >
                Approve {editedRows.length > 0 && `(${editedRows.length})`}
              </HeaderButtons>
            )
          }
          buttonContent="Approve Selected Reimbursements"
        />
        {viewModal && (
          <ViewReimbursement
            selectedRow={selectedRow}
            columns={tableColumns}
            onClose={handleModalClose}
          />
        )}
        {openEditModal && (
          <GenericModal
            isOpen={openEditModal}
            onClose={handleModalClose}
            header="Edit Reimbursement"
            customWidth="900px"
          >
            <UpdateReimbursement
              selectedRow={selectedRow}
              onClose={handleModalClose}
              onSuccess={onSuccess}
              onError={onError}
            />
          </GenericModal>
        )}
        {openDeleteModal && (
          <GenericModal
            isOpen={openDeleteModal}
            onClose={() => setDeleteModal(false)}
            header="Delete Reimbursement"
          >
            <DeletePopup
              handleDelete={deleteRow}
              onClose={() => setDeleteModal(false)}
              selectedRow={selectedRow}
            />
          </GenericModal>
        )}
        {(showSuccessToaster || showErrorToaster) && (
          <GenericToaster
            message={message}
            onClose={closeToaster}
            open={showSuccessToaster || showErrorToaster}
            severity={showErrorToaster ? "error" : "info"}
          />
        )}
      </div>
    </div>
  );
}
